import BaseListStore, { DEFAULT_LIMIT, DEFAULT_PAGE_NUMBER } from 'stores/BaseListStore';
import DeviceStore from 'stores/device/DeviceStore';
import { IDataFetchOptions, IGetDataParams } from '../../types';
import {
    buildOptions,
    buildWhereFilter,
    destroy,
    EntityName,
    get,
    getCount,
    getDetailsForCorrectPagination,
    getPage,
    patch,
    post
} from 'stores/_services/dataService';
import { buildCsvDownloadUrl } from 'stores/_services/downloadService';
import { getConditions, getItemListConditions, getListConditions } from 'stores/_services/filterService';
import { SortDirection } from 'constants/SortDirection';
import { action, makeObservable, observable, override, runInAction, toJS } from 'mobx';
import { ScanType } from 'constants/ScanType';

const SEARCH_FIELDS = ['name', 'hostname', 'ip'];
let filterUpdated = true;

export default class DeviceListStore extends BaseListStore<DeviceStore> {
    filteredCount = 0;
    baseRoute = 'agents';

    constructor() {
        super({
            itemStore: DeviceStore,
            sortBy: 'name',
            sortDirection: SortDirection.ASC
        });

        makeObservable(this, {
            clearIds: override,
            filteredCount: observable,
            enableAgentsByQuery: action,
            installAgents: action,
            uninstallAgents: action,
            uninstallAgent: action
        });
    }

    async getDevices(args: IDataFetchOptions = {}, filterUpdate: boolean, pageNumber = DEFAULT_PAGE_NUMBER, pageSize = DEFAULT_LIMIT): Promise<void> {
        if (!filterUpdated) filterUpdated = filterUpdate;

        const shouldReset = this._updateListState(args);
        const conditions = getConditions(this.filter);

        const options = {
            filter: {
                searchFields: SEARCH_FIELDS,
                order: this.sort,
                limit: pageSize,
                skip: (pageNumber - 1) * pageSize,
                where: conditions,
                include: [
                    {
                        relation: 'organization',
                        scope: {
                            fields: ['name', 'data']
                        }
                    },
                    {
                        relation: 'location',
                        scope: {
                            fields: ['name', 'data']
                        }
                    }
                ]
            }
        };

        const { data } = await getPage(EntityName.AGENT_DETAILS, options);
        const totalCount = await getCount(EntityName.AGENT_DETAILS, { where: conditions, searchFields: SEARCH_FIELDS });

        if (shouldReset) {
            this.clearIds();
        }

        this._updateData(data);
        this._updateIds(data, 0);
        this.count = data.length;
        this.totalCount = totalCount;
        this.page = pageNumber;
        this.pageSize = pageSize;

        if (filterUpdated) {
            runInAction(() => {
                this.filteredCount = totalCount;
                filterUpdated = false;
            });
        }
    }

    async getDevicesForCorrectPagination(args: IGetDataParams = {}): Promise<void> {
        const { filter, sortBy, sortDirection, pageSize, page } = args;

        const include = [
            {
                relation: 'organization',
                scope: {
                    fields: ['name', 'data']
                }
            },
            {
                relation: 'location',
                scope: {
                    fields: ['name', 'data']
                }
            }
        ];

        const params = {
            filter: filter || this.filter,
            sortBy: sortBy || this.sortBy,
            sortDirection: sortDirection || this.sortDirection,
            pageSize: pageSize || this.pageSize,
            page: page || this.page,
            searchFields: SEARCH_FIELDS,
            include
        };

        const { data, totalCount } = await getDetailsForCorrectPagination<DeviceListStore>({ params, entityName: EntityName.AGENT_DETAILS });

        runInAction(() => {
            this.filteredCount = totalCount;
        });

        this.updateStateForCorrectPagination<DeviceListStore>({ args, data, totalCount });
    }

    async getDeviceData(id: string): Promise<void> {
        const options = {
            filter: {
                include: [
                    {
                        relation: 'organization',
                        scope: {
                            fields: ['name', 'data']
                        }
                    },
                    {
                        relation: 'location',
                        scope: {
                            fields: ['name', 'data']
                        }
                    }
                ]
            }
        };
        const { data } = await get(`${EntityName.AGENT_DETAILS}/${id}`, options);

        this._updateData(data);
    }

    async existsDeviceName(name: string, locationId: string): Promise<boolean> {
        const options = {
            filter: {
                where: { name: name, locationId: locationId }
            }
        };
        const { data } = await get(this.baseRoute, options);
        return !!data.length;
    }

    async renameDevice(id: string, name: string, locationId: string): Promise<boolean> {
        const exists = await this.existsDeviceName(name, locationId);
        if (exists) {
            return false;
        }
        const payload = {
            name: name
        };
        await patch(`${this.baseRoute}/${id}/rename`, payload);
        await this.getDevices({ reset: true }, true);
        return true;
    }

    async deleteDevice(id: string): Promise<void> {
        await destroy(`${this.baseRoute}/${id}`);
        await this.getDevices({ reset: true }, true);
    }

    async deleteDevices(id?: string): Promise<void> {
        const conditions = getListConditions(id ? [id] : this.selectedIdList, this.filter);
        const options = {
            searchFields: SEARCH_FIELDS,
            where: conditions
        };
        await destroy('agents', options);
        this.selectedIds = {};
        await this.getDevices({ reset: true }, true);
    }

    async moveDevice(id: string, organizationId: string, locationId: string, name: string): Promise<boolean> {
        const exists = await this.existsDeviceName(name, locationId);
        const newName = exists ? `${name}_` : name;
        const payload = {
            name: newName,
            organizationId: organizationId,
            locationId: locationId
        };
        await patch(`${this.baseRoute}/${id}`, payload);
        await this.getDevices({ reset: true }, true);
        return !exists;
    }

    async moveDevices(ids: Array<string>, organizationId: string, locationId: string): Promise<void> {
        const payload = ids.map((id) => ({
            id: id,
            organizationId: organizationId,
            locationId: locationId
        }));
        await patch(`${this.baseRoute}/bulkSave`, payload);
        this.selectedIds = {};
        await this.getDevices({ reset: true }, true);
    }

    async moveDevices2(id: string, organizationId: string, locationId: string): Promise<void> {
        const conditions = getListConditions(id ? [id] : this.selectedIdList, this.filter);
        const where = buildWhereFilter({
            searchFields: SEARCH_FIELDS,
            where: conditions
        });
        const filter = { where: where };
        await post(`${this.baseRoute}/assignTarget`, { targetId: locationId, filter });
        this.selectedIds = {};
        await this.getDevices({ reset: true }, true);
    }

    async enableAgentsByQuery(conditions: any): Promise<void> {
        const where = buildWhereFilter({
            where: conditions,
            searchFields: SEARCH_FIELDS
        });

        await post(`${this.baseRoute}/approve`, { where });
    }

    async installAgents(conditions: any) {
        const where = buildWhereFilter({
            where: conditions,
            searchFields: SEARCH_FIELDS
        });

        await post(`${this.baseRoute}/install`, { where });

        this.selectedIds = {};
        await this.getDevices({ reset: true }, true);
    }

    async uninstallAgents(conditions: any): Promise<void> {
        const where = buildWhereFilter({
            where: conditions,
            searchFields: SEARCH_FIELDS
        });

        await post(`${this.baseRoute}/uninstall`, { where });

        this.selectedIds = {};
        await this.getDevices({ reset: true }, true);
    }

    async uninstallAgent(agentId: string): Promise<void> {
        await post(`${this.baseRoute}/${agentId}/uninstall`);
        this.selectedIds = {};
        await this.getDevices({ reset: true }, true);
    }

    async exportSelectedItems(ids?: Array<string>): Promise<void> {
        const conditions = getItemListConditions(ids ? ids : this.selectedIdList);

        const options = {
            filter: {
                fileName: 'DeviceDetail',
                searchFields: SEARCH_FIELDS,
                order: this.sort,
                where: conditions,
                include: [
                    {
                        relation: 'organization',
                        scope: {
                            fields: ['name', 'data']
                        }
                    },
                    {
                        relation: 'location',
                        scope: {
                            fields: ['name', 'data']
                        }
                    }
                ]
            }
        };

        const query = buildOptions(options);

        window.location.href = await buildCsvDownloadUrl(EntityName.AGENT_DETAILS, query);
    }

    async deviceIsolationState(ids: string[], locationId: string | undefined, action: string): Promise<void> {
        await post(`${this.baseRoute}/toggleIsolation`, { targetId: locationId, action, agentIds: ids });
    }

    async exportAllDevices(filter?: any): Promise<void> {
        const conditions = getConditions(filter ? filter : this.filter);

        const options = {
            filter: {
                searchFields: SEARCH_FIELDS,
                order: this.sort,
                where: conditions,
                include: [
                    {
                        relation: 'organization',
                        scope: {
                            fields: ['name', 'data']
                        }
                    },
                    {
                        relation: 'location',
                        scope: {
                            fields: ['name', 'data']
                        }
                    }
                ]
            }
        };

        await get(`${this.baseRoute}/export`, options);
    }

    async assignLicenses(payload: any) {
        if (payload.filter) {
            const conditions = getConditions(payload.filter);
            payload.where = buildWhereFilter({
                where: conditions,
                searchFields: SEARCH_FIELDS
            });
        }

        const { data } = await post(`${this.baseRoute}/assignLicenses`, payload);
        return data;
    }

    async unassignLicenses(payload: any) {
        if (payload.filter) {
            const conditions = getConditions(payload.filter);
            payload.where = buildWhereFilter({
                where: conditions,
                searchFields: SEARCH_FIELDS
            });
        }

        const { data } = await post(`${this.baseRoute}/unassignLicenses`, payload);
        return data;
    }

    async assignDeviceGroup(payload: any) {
        payload.filter = payload.filter || {};

        if (payload.agentIds?.length) {
            payload.filter = { id: { inq: payload.agentIds } };
        } else {
            const conditions = getConditions(payload.filter);
            payload.filter = buildWhereFilter({ where: conditions }) || {};
        }

        const { data } = await post('agents/assignDeviceGroup', payload);
        return data;
    }

    async unassignDeviceGroup(payload: any) {
        payload.filter = payload.filter || {};

        if (payload.agentIds?.length) {
            payload.filter = { id: { inq: payload.agentIds } };
        } else {
            const conditions = getConditions(payload.filter);
            payload.filter = buildWhereFilter({ where: conditions }) || {};
        }

        const { data } = await post('agents/unassignDeviceGroup', payload);
        return data;
    }

    getLicenseStatus(device: any) {
        let licenses = [];

        if (device.hasEdrLicense) {
            licenses.push('EDR');
        }

        if (device.hasAvLicense) {
            licenses.push('AV');
        }

        if (licenses.length) {
            return licenses.join(', ');
        } else {
            return 'Unlicensed';
        }
    }

    async fetchLogs(agentId: string): Promise<boolean> {
        try {
            await post(`${this.baseRoute}/${agentId}/retrieveLogs`);
            return true;
        } catch (error) {
            return false;
        }
    }

    async scanDevices({ deviceIds, scanType, options, taskName }: { deviceIds: string[]; scanType: ScanType; options: any; taskName?: string }): Promise<void> {
        let conditions: any[] = [];
        if (scanType === ScanType.FILTERED) {
            conditions = getConditions(this.filter);
        }
        if (deviceIds && deviceIds.length) {
            conditions.push({
                id: deviceIds
            });
        }

        const where = buildWhereFilter({
            where: conditions,
            searchFields: SEARCH_FIELDS
        });
        if (options?.extensions) {
            options.extensions = toJS(options.extensions).map((i: any, idx: number) => ({
                id: i.id,
                name: i.name,
                order: idx
            }));
        }
        await post('agents/scan', { where, options, taskName });
    }
}
